import { apolloClient } from '@/app/providers/apollo';
import { isTechnicalMessage, sanitizeMessage } from '@/shared/lib/format-error';
import {
  ChangePasswordDocument,
  LoginDocument,
  LogoutDocument,
  RefreshDocument,
} from '../__generated__/graphql';
import { sessionSchema, type Session } from '../schemas/session.schema';

export interface LoginInput {
  email: string;
  password: string;
}

export class LoginError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LoginError';
  }
}

export const INVALID_CREDENTIALS = 'invalid_credentials';

interface UpstreamBody {
  error?: unknown;
}
interface UpstreamExtensions {
  statusCode?: unknown;
  response?: { status?: unknown; body?: UpstreamBody };
}
export interface ApolloErrorLike {
  message?: unknown;
  graphQLErrors?: readonly { extensions?: UpstreamExtensions }[];
}

const extractUpstreamMessage = (err: unknown): string | undefined => {
  if (!err || typeof err !== 'object') {
    return undefined;
  }
  const gqlError = (err as ApolloErrorLike).graphQLErrors?.[0];
  const bodyError = gqlError?.extensions?.response?.body?.error;
  return typeof bodyError === 'string' ? bodyError : undefined;
};

const isUnauthorized = (err: unknown): boolean => {
  if (!err || typeof err !== 'object') {
    return false;
  }
  const gqlError = (err as ApolloErrorLike).graphQLErrors?.[0];
  const status = gqlError?.extensions?.response?.status ?? gqlError?.extensions?.statusCode;
  if (status === 401) {
    return true;
  }
  const message = (err as ApolloErrorLike).message;
  return typeof message === 'string' && /Upstream HTTP Error:\s*401\b/.test(message);
};

const toLoginError = (err: unknown, fallback: string): LoginError => {
  const upstreamMessage = extractUpstreamMessage(err);
  if (upstreamMessage && !isTechnicalMessage(upstreamMessage)) {
    return new LoginError(upstreamMessage);
  }
  if (isUnauthorized(err)) {
    return new LoginError(INVALID_CREDENTIALS);
  }
  if (upstreamMessage) {
    return new LoginError(sanitizeMessage(upstreamMessage));
  }
  return new LoginError(err instanceof Error ? sanitizeMessage(err.message) : fallback);
};

export const login = async (input: LoginInput): Promise<Session> => {
  try {
    const result = await apolloClient.mutate({ mutation: LoginDocument, variables: { input } });
    return sessionSchema.parse(result.data?.login);
  } catch (err) {
    throw toLoginError(err, 'Authentication failed');
  }
};

export const refreshToken = async (refreshToken: string): Promise<Session> => {
  try {
    const result = await apolloClient.mutate({
      mutation: RefreshDocument,
      variables: { input: { refresh_token: refreshToken } },
    });
    return sessionSchema.parse(result.data?.refresh);
  } catch {
    throw new LoginError('Session expired');
  }
};

export const logout = async (refreshToken?: string): Promise<void> => {
  try {
    await apolloClient.mutate({
      mutation: LogoutDocument,
      variables: { input: refreshToken ? { refresh_token: refreshToken } : {} },
    });
  } catch {}
};

export type ChangePasswordFailure =
  | 'wrong_password'
  | 'unauthorized'
  | 'invalid_password'
  | 'error';

export const changePassword = async (input: {
  currentPassword: string;
  newPassword: string;
}): Promise<{ ok: true } | { ok: false; failure: ChangePasswordFailure; message?: string }> => {
  try {
    await apolloClient.mutate({
      mutation: ChangePasswordDocument,
      variables: {
        input: {
          current_password: input.currentPassword,
          new_password: input.newPassword,
        },
      },
    });
    return { ok: true };
  } catch (err) {
    const status = upstreamStatus(err);
    if (status === 401) {
      return extractUpstreamMessage(err) === 'Current password is incorrect'
        ? { ok: false, failure: 'wrong_password' }
        : { ok: false, failure: 'unauthorized' };
    }
    if (status === 400) {
      const upstreamMessage = extractUpstreamMessage(err);
      const message =
        upstreamMessage && !isTechnicalMessage(upstreamMessage)
          ? sanitizeMessage(upstreamMessage)
          : undefined;
      return message === undefined
        ? { ok: false, failure: 'invalid_password' }
        : { ok: false, failure: 'invalid_password', message };
    }
    return { ok: false, failure: 'error' };
  }
};

export const upstreamStatus = (err: unknown): number | null => {
  if (!err || typeof err !== 'object') {
    return null;
  }
  const gqlError = (err as ApolloErrorLike).graphQLErrors?.[0];
  const status = gqlError?.extensions?.response?.status ?? gqlError?.extensions?.statusCode;
  if (typeof status === 'number') {
    return status;
  }
  const message = (err as { message?: unknown }).message;
  if (typeof message === 'string') {
    const match = /Upstream HTTP Error:\s*(\d+)/.exec(message);
    if (match) {
      return Number(match[1]);
    }
  }
  return null;
};
